import { styled } from '@styledComponents';
import { themeSpace, themeMediaQuery, themeColor, themeVariant } from '@utils/styledTheme.util';
import { CloudImage } from '@components/CloudImage';
import { TextPairing } from '@components/TextPairing';
import { Columns } from '@components/Columns';
import { Flex } from '@components/Flex';
import { VideoPlayer } from '@components/VideoPlayer';

export const StyledSection = styled.div`
  height: 100vh;
  min-height: var(--hero-min-height);
  position: relative;
  z-index: 1;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const HeroVideoContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transform-origin: center center;
`;

export const SlurpeeBgStrip = styled(CloudImage)`
  z-index: 3;
`;

export const Step2TextWrapper = styled.div`
  position: absolute;
  bottom: 23%;
  left: 50%;
  transform: translate(-50%, 500px);
  max-width: 440px;

  h3 {
    color: ${themeColor('mono1')};
  }

  ${themeMediaQuery('xl')} {
    max-width: 520px;
    transform: translate(-50%, 490px);
  }

  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

const lottieBgWidth = 1620;
const lottieBgHeight = 1080;
const lottieBgWhRatioStr = `${lottieBgWidth} / ${lottieBgHeight}`;
const lottieBgWhRatio = lottieBgWidth / lottieBgHeight;
const lottieWhRatio = 1620 / 709;
const lottieAnimationToBgHRatio = lottieBgWhRatio / lottieWhRatio;

export const HeroBodymovinWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export const HeroLottieContainer = styled.div`
  display: flex;
  width: calc(100vh * ${lottieBgWhRatio});
  height: calc(100vh * ${lottieAnimationToBgHRatio});
  @media (min-aspect-ratio: ${lottieBgWhRatioStr}) {
    width: 100vw;
    height: calc(100vw / ${lottieWhRatio});
  }
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%);
`;

export const HeroVideoWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  transform-origin: center center;
  overflow: hidden;
  > video {
    width: 100vw;
    height: 100vh;
    object-fit: cover;
    position: absolute;
    left: 0;
  }
  #homepage-hero-video-overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    background-color: #000;
    z-index: 3;
    opacity: 0.6;
  }
  > img {
    width: calc(100vh * ${lottieBgWhRatio});
    height: 100vh;
    @media (min-aspect-ratio: ${lottieBgWhRatioStr}) {
      width: 100vw;
      height: calc(100vw / ${lottieBgWhRatio});
    }
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translate(-50%);
  }
  .home-hero-lottie-element {
    opacity: 0;
    z-index: 4;
  }
`;

/**
 * Hero text block with optimized line lengths for readability.
 * - Title: ~20-25 characters per line (display text)
 * - Body: ~55 characters per line (optimal reading length)
 */
export const HeroTextPairing = styled(TextPairing)<{ id: string }>`
  --hero-text-max-width: 670px;

  max-width: var(--hero-text-max-width);
  margin-bottom: ${themeSpace(8)}; /* 40px (2.5rem) */
  ${themeMediaQuery('below_sm')} {
    margin-bottom: ${themeSpace(7)}; /* 32px (2rem) */
  }

  .text-pairing-title {
    max-width: 77%; /* ~516px of 670px container */
    margin-bottom: ${themeSpace(5)};
    ${themeMediaQuery('below_sm')} {
      max-width: 85%;
    }
    ${themeMediaQuery('xl')} {
      max-width: 94%;
      ${themeVariant('hed56')}; /* Match lg breakpoint */
    }
  }

  .text-pairing-body {
    max-width: 55ch; /* Optimal reading line length */
    ${themeMediaQuery('mobile')} {
      max-width: 100%;
    }
    ${themeMediaQuery('below_sm')} {
      max-width: 45ch;
      ${themeVariant('featureCopy17')};
    }
    ${themeMediaQuery('xl')} {
      max-width: 60ch;
      ${themeVariant('dek20')}; /* Match lg breakpoint */
    }
  }
`;

export const Step1TextBlockWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${themeSpace(5)};
`;

export const ButtonsContainer = styled(Flex)`
  gap: ${themeSpace(7)}; /* 32px */
  max-width: 620px;
  justify-content: center;
  ${themeMediaQuery('below_sm')} {
    max-width: 100%;
    gap: ${themeSpace(5)}; /* 16px on mobile */
  }
`;

export const uiMagicXAxisShift = '30vw';
export const uiMagicYAxisShift = '30vh';
export const initialScale = 1.5;

export const UIMagicWrapper = styled.div`
  background-color: ${themeColor('mono7')};
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  > img,
  > .svg-wrapper {
    opacity: 0;
    position: absolute;
    transform-origin: center center;
    will-change: transform;
    max-width: 30%;
    max-height: 30%;
    &.ui-magic-img-top {
      top: 0;
      transform: translate(0, -${uiMagicYAxisShift}) scale(${initialScale});
    }
    &.ui-magic-img-top-right {
      right: 0;
      top: 0;
      transform: translate(${uiMagicXAxisShift}, -${uiMagicYAxisShift}) scale(${initialScale});
    }
    &.ui-magic-img-right {
      right: 0;
      transform: translate(${uiMagicXAxisShift}, 0) scale(${initialScale});
    }
    &.ui-magic-img-bottom-right {
      right: 0;
      bottom: 0;
      transform: translate(${uiMagicXAxisShift}, ${uiMagicYAxisShift}) scale(${initialScale});
    }
    &.ui-magic-img-bottom {
      bottom: 0;
      transform: translate(0, ${uiMagicYAxisShift}) scale(${initialScale});
    }
    &.ui-magic-img-bottom-left {
      left: 0;
      bottom: 0;
      transform: translate(-${uiMagicXAxisShift}, ${uiMagicYAxisShift}) scale(${initialScale});
    }
    &.ui-magic-img-left {
      left: 0;
      transform: translate(-${uiMagicXAxisShift}, 0) scale(${initialScale});
    }
    &.ui-magic-img-top-left {
      left: 0;
      top: 0;
      transform: translate(-${uiMagicXAxisShift}, -${uiMagicYAxisShift}) scale(${initialScale});
    }
  }
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const MobBoxContainer = styled.div`
  width: 100%;
  height: 92vh;
  position: relative;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${themeMediaQuery('mobile')} {
    height: 90vh;
    padding-left: ${themeSpace(7)};
    padding-right: ${themeSpace(7)};
  }
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const HeroVideo = styled(VideoPlayer)`
  position: absolute;
  height: 100%;
`;

export const OverlayDiv = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: #000;
  opacity: 0.6;
`;

export const TextWrapper = styled(Columns)`
  width: 100%;
  height: 100%;
  position: absolute;
  ${themeMediaQuery('below_sm')} {
    margin-top: 2rem;
    padding-left: 0rem;
    padding-right: 0rem;
    > div {
      margin-left: 0rem;
      margin-right: 0rem;
    }
  }
`;

export const FlexContainer = styled(Flex)`
  height: 100%;
  justify-content: center;
  ${themeMediaQuery('mobile')} {
    padding-left: ${themeSpace(6)};
    padding-right: ${themeSpace(6)};
  }
`;

export const ColumnContent = styled(Columns.Column)`
  height: 100%;
  width: 100%;
`;

export const WaveImgContainer = styled.div`
  width: 100%;
  position: absolute;
  bottom: -3px;
  z-index: 3;
  img {
    margin-bottom: -3px;
  }
`;

export const DesktopBottomWaveImage = styled(CloudImage)`
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const MobileBottomWaveImage = styled(CloudImage)`
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;
